import React from 'react';
import styled from '@benzinga/themetron';
import { AltOffering } from '@benzinga/alts-manager';
import Image from 'next/image';
import { sanitizeHTML } from '@benzinga/frontend-utils';

interface OfferingDetailsProps {
  listing: AltOffering;
}

export const OfferingDetails: React.FC<OfferingDetailsProps> = ({ listing }) => {
  const investmentTargets = [
    { label: 'Target IRR', value: 'listing.target_irr' },
    { label: 'Target Average Annual Cash Yield', value: 'listing.target_aacy' },
    { label: 'Target Investment Term', value: 'listing.target_investment_term' },
  ];

  const listingDetails = [
    { label: 'Minimum Investment', value: `$${listing.minInvestment}` },
    { label: 'Property Type(s)', value: listing.propertyType },
    { label: 'Investment Type(s)', value: 'listing.investment_types' },
  ];

  return (
    <PlatformDetailsContainer className="offering-details-container">
      <div className="details-left-side">
        <div className="description-block">
          <h5>Description</h5>
          <p dangerouslySetInnerHTML={{ __html: sanitizeHTML(listing.description || '') }}></p>
        </div>
        <div className="platform-details-block">
          <h5>Investment Targets</h5>
          <ul className="platform-details-list">
            {investmentTargets.map(item => (
              <li key={item.label}>
                <span>{item.label}</span>
                <span>{item.value}</span>
              </li>
            ))}
          </ul>
        </div>
        {listing.company && (
          <div className="offer-provided-by-block">
            <h5>Offered Provided By:</h5>
            <div>
              {listing.company.image ? (
                <a
                  className="listing-logo"
                  href={`/go/${listing.company?.slug}`}
                  rel="nofollow noreferrer"
                  target="_blank"
                >
                  <Image alt={listing.platform} height={40} src={listing.company.image} width={40} />
                </a>
              ) : (
                <div className="listing-logo" />
              )}
              <a href={`/go/${listing.company?.slug}`} rel="nofollow noreferrer" target="_blank">
                <h6>{listing.platform}</h6>
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="details-right-side">
        <div className="listing-details-block">
          <h5>{listing.name}</h5>
          <ul className="listing-details-list">
            {listingDetails.map(item => (
              <li key={item.label}>
                <span>{item.label}</span>
                <span>{item.value}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="minimum-investment-block">
          <h5>Minimum Investment</h5>
          <div>
            <p>Minimum Investment</p>
            <p>${listing.minInvestment}</p>
          </div>
        </div>
        <div className="available-to-block">
          <h5>Available to:</h5>
          <p>
            {' '}
            {listing.nonAccredited ? 'Available to Non-Accredited Investors' : 'Available to Accredited Investors'}
          </p>
        </div>
      </div>
    </PlatformDetailsContainer>
  );
};

const PlatformDetailsContainer = styled.div`
  &.offering-details-container {
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 992px) {
      flex-direction: row;
    }

    .details-left-side {
      margin-right: 25px;
    }

    .platform-details-block,
    .listing-details-block {
      ul {
        display: flex;
        flex-direction: column;

        li {
          display: flex;
          justify-content: space-between;
          padding-bottom: 8px;
          box-sizing: border-box;
          padding-top: 8px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.1);

          &:first-child {
            padding-top: 0;
          }

          &:last-of-type {
            border-bottom: none;
          }
        }
      }
    }

    @media screen and (min-width: 992px) {
      .details-left-side {
        width: 60%;
      }

      .details-right-side {
        width: 40%;
      }
    }

    .details-left-side,
    .details-right-side {
      font-size: 14px;
      width: 100%;

      > div {
        background: #ffffff;
        padding: 20px;
        margin-bottom: 25px;
        border: 1px solid #ebebeb;
        border-radius: 5px;

        h5 {
          font-weight: 600;
          color: #242429;
          margin-bottom: 15px;
          letter-spacing: 0;
          font-size: 14px;
        }
      }
    }

    p {
      color: #242429;
      font-size: 14px;
      line-height: 26px;
    }

    .minimum-investment-block {
      > div {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }
    }

    .offer-provided-by-block {
      > div {
        display: flex;
        align-items: center;
      }

      .listing-logo {
        width: 40px;
        height: 40px;
        background: #dde5e6 50% no-repeat;
        background-size: cover;
        display: inline-block;
        -webkit-transition: all 0.15s ease-in-out;
        transition: all 0.15s ease-in-out;
        border-radius: 100%;
        margin-right: 8px;
      }
    }
  }
`;
